import React, { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { AdminTherapistRole } from "../api/contracts";
import { ClinicianRoleSelect } from "../components/ClinicianRoleSelect";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { useAdminDirectory } from "../hooks/useAdminDirectory";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

interface FormErrors {
  therapistName?: string;
  email?: string;
  role?: string;
  status?: string;
  clinics?: string;
}

function safeApiMessage(error: ClinicianPortalApiError, fallback: string) {
  return error.message.trim() || fallback;
}

function updateErrorMessage(error: unknown): FormErrors & { form?: string } {
  if (!(error instanceof ClinicianPortalApiError)) {
    return { form: "Therapist could not be saved. Try again." };
  }

  switch (error.code) {
    case "DUPLICATE_THERAPIST":
      return { email: "A therapist with this email already exists." };
    case "INVALID_THERAPIST_NAME":
      return {
        therapistName: safeApiMessage(error, "Enter a valid therapist name."),
      };
    case "INVALID_EMAIL":
      return { email: safeApiMessage(error, "Enter a valid email address.") };
    case "INVALID_ROLE":
      return { role: safeApiMessage(error, "Select a valid role.") };
    case "INVALID_STATUS":
      return { status: safeApiMessage(error, "Select a valid status.") };
    case "CLINIC_NOT_FOUND":
      return {
        clinics: safeApiMessage(
          error,
          "One or more selected clinics are no longer available. Refresh and try again.",
        ),
      };
    case "NOT_AUTHORIZED":
    case "NOT_FOUND":
      return { form: "This therapist is unavailable." };
    default:
      return {
        form: safeApiMessage(error, "Therapist could not be saved. Try again."),
      };
  }
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function EditTherapistPage({
  clinicianCode,
  therapistId,
}: {
  clinicianCode: string;
  therapistId: string;
}) {
  const directoryState = useAdminDirectory(clinicianCode);
  const mutation = usePortalMutation();
  const [therapistName, setTherapistName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminTherapistRole>("therapist");
  const [status, setStatus] = useState("Active");
  const [advancedDashboardEnabled, setAdvancedDashboardEnabled] =
    useState(true);
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const directory =
    directoryState.status === "success" ||
    directoryState.status === "refreshing"
      ? directoryState.data
      : undefined;
  const therapist = useMemo(
    () =>
      directory?.therapists.therapists.find((item) => item.id === therapistId),
    [directory, therapistId],
  );
  const assignedClinicIds = useMemo(
    () =>
      directory?.clinics.clinics
        .filter((clinic) => clinic.therapistIds.includes(therapistId))
        .map((clinic) => clinic.id) ?? [],
    [directory, therapistId],
  );
  const available = Boolean(
    directory?.clinics.found &&
      directory.clinics.authorized &&
      directory.clinics.therapist?.role === "admin" &&
      directory.therapists.found &&
      directory.therapists.authorized &&
      directory.therapists.therapist?.role === "admin",
  );

  useEffect(() => {
    if (!therapist || initialized) return;
    setTherapistName(therapist.therapistName);
    setEmail(therapist.email);
    const existingRole = therapist.role.trim().toLocaleLowerCase();
    setRole(
      existingRole === "admin" || existingRole === "supervisor"
        ? existingRole
        : "therapist",
    );
    setStatus(therapist.status || "Active");
    setAdvancedDashboardEnabled(therapist.advancedDashboardEnabled ?? true);
    setSelectedClinicIds(assignedClinicIds);
    setInitialized(true);
  }, [assignedClinicIds, initialized, therapist]);

  const isSubmitting = mutation.state.status === "pending";
  const apiErrors =
    mutation.state.status === "error"
      ? updateErrorMessage(mutation.state.error)
      : {};
  const errors = { ...validationErrors, ...apiErrors };

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const toggleClinic = (clinicId: string) => {
    clearErrors();
    setSelectedClinicIds((current) =>
      current.includes(clinicId)
        ? current.filter((id) => id !== clinicId)
        : [...current, clinicId],
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedName = therapistName.trim();
    const normalizedEmail = email.trim().toLocaleLowerCase();
    const nextErrors: FormErrors = {};
    if (!normalizedName)
      nextErrors.therapistName = "Therapist name is required.";
    if (!normalizedEmail) nextErrors.email = "Email is required.";
    else if (!isValidEmail(normalizedEmail)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (role !== "therapist" && role !== "supervisor" && role !== "admin") {
      nextErrors.role = "Select a valid role.";
    }
    if (status !== "Active" && status !== "Inactive") {
      nextErrors.status = "Select a valid status.";
    }
    if (!selectedClinicIds.length) {
      nextErrors.clinics = "Select at least one clinic.";
    }
    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    const result = await mutation.run("update-therapist", (signal) =>
      clinicianPortalApi.updateTherapist(
        clinicianCode,
        therapistId,
        {
          therapistName: normalizedName,
          email: normalizedEmail,
          role,
          status,
          clinicIds: selectedClinicIds,
          advancedDashboardEnabled,
        },
        signal,
      ),
    );
    if (result?.updated) {
      navigateWithinPortal(
        withClinicianCode("/clinician-portal/admin/therapists", clinicianCode),
      );
    }
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="therapists"
      clinicianCode={clinicianCode}
      therapistRole={directory?.therapists.therapist?.role}
    >
      <PortalHeader
        title={therapist?.therapistName || "Edit Therapist"}
        clinicianName={
          available ? directory?.therapists.therapist?.therapistName : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Administration" },
              {
                label: "Therapists",
                path: "/clinician-portal/admin/therapists",
              },
              { label: "Edit Therapist" },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        {directoryState.status === "loading" ? (
          <LoadingState label="Loading therapist" />
        ) : null}
        {directoryState.status === "error" ? (
          <ErrorState
            error={directoryState.error}
            onRetry={directoryState.refresh}
          />
        ) : null}
        {directory && !available ? (
          <ErrorState error={new Error("Therapist editing is unavailable")} />
        ) : null}
        {directory && available && !therapist ? (
          <ErrorState error={new Error("Therapist not found")} />
        ) : null}
        {directory && available && therapist && initialized ? (
          <section
            className="cp-record-form"
            aria-labelledby="edit-therapist-heading"
          >
            <div className="cp-record-form__header">
              <h2 id="edit-therapist-heading">Edit Therapist</h2>
              <p>Update the clinician account and clinic assignments.</p>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="cp-form-field">
                <label htmlFor="therapist-name">
                  Therapist Name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="therapist-name"
                  className="cp-form-input"
                  type="text"
                  value={therapistName}
                  required
                  aria-invalid={Boolean(errors.therapistName)}
                  aria-describedby={
                    errors.therapistName ? "therapist-name-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setTherapistName(event.target.value);
                  }}
                />
                {errors.therapistName ? (
                  <p className="cp-form-error" id="therapist-name-error">
                    {errors.therapistName}
                  </p>
                ) : null}
              </div>

              <div className="cp-form-field">
                <label htmlFor="therapist-email">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  id="therapist-email"
                  className="cp-form-input"
                  type="email"
                  value={email}
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={
                    errors.email ? "therapist-email-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setEmail(event.target.value);
                  }}
                />
                {errors.email ? (
                  <p className="cp-form-error" id="therapist-email-error">
                    {errors.email}
                  </p>
                ) : null}
              </div>

              <div className="cp-form-field">
                <label htmlFor="therapist-code">Clinician Code</label>
                <input
                  id="therapist-code"
                  className="cp-form-input"
                  type="text"
                  value={therapist.clinicianCode}
                  readOnly
                />
              </div>

              <div className="cp-form-field">
                <label htmlFor="therapist-role">
                  Role <span aria-hidden="true">*</span>
                </label>
                <ClinicianRoleSelect
                  id="therapist-role"
                  value={role}
                  invalid={Boolean(errors.role)}
                  disabled={isSubmitting}
                  onChange={(nextRole) => {
                    clearErrors();
                    setRole(nextRole);
                  }}
                />
              </div>

              <div className="cp-form-field">
                <label htmlFor="therapist-status">
                  Status <span aria-hidden="true">*</span>
                </label>
                <select
                  id="therapist-status"
                  className="cp-form-input"
                  value={status}
                  required
                  aria-invalid={Boolean(errors.status)}
                  onChange={(event) => {
                    clearErrors();
                    setStatus(event.target.value);
                  }}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="cp-form-field">
                <span className="cp-lov-checkbox-label">Homepage</span>
                <label
                  className="cp-form-checkbox"
                  htmlFor="therapist-advanced-dashboard"
                >
                  <input
                    id="therapist-advanced-dashboard"
                    type="checkbox"
                    checked={advancedDashboardEnabled}
                    disabled={isSubmitting}
                    aria-describedby="therapist-advanced-dashboard-help"
                    onChange={(event) => {
                      clearErrors();
                      setAdvancedDashboardEnabled(event.target.checked);
                    }}
                  />
                  Advanced Dashboard
                </label>
                <p
                  className="cp-form-help"
                  id="therapist-advanced-dashboard-help"
                >
                  Show Safety Events and Caseload Engagement cards on the
                  clinician homepage.
                </p>
              </div>

              <fieldset
                className="cp-form-field cp-form-fieldset"
                aria-describedby={errors.clinics ? "clinics-error" : undefined}
                aria-invalid={Boolean(errors.clinics)}
                aria-required="true"
              >
                <legend>
                  Clinics <span aria-hidden="true">*</span>
                </legend>
                {directory.clinics.clinics.length ? (
                  <div className="cp-checkbox-list">
                    {directory.clinics.clinics.map((clinic) => (
                      <label className="cp-checkbox-option" key={clinic.id}>
                        <input
                          type="checkbox"
                          checked={selectedClinicIds.includes(clinic.id)}
                          onChange={() => toggleClinic(clinic.id)}
                        />
                        <span>
                          <strong>{clinic.clinicName || "—"}</strong>
                          <small>{clinic.status || "—"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-form-empty">
                    No clinics are currently available. Create a clinic before
                    saving this therapist.
                  </p>
                )}
                {errors.clinics ? (
                  <p className="cp-form-error" id="clinics-error">
                    {errors.clinics}
                  </p>
                ) : null}
              </fieldset>

              {apiErrors.form ? (
                <p className="cp-form-error cp-form-error--panel" role="alert">
                  {apiErrors.form}
                </p>
              ) : null}

              <div className="cp-form-actions cp-form-actions--sticky">
                <PortalLink
                  className="cp-button cp-button--secondary"
                  path="/clinician-portal/admin/therapists"
                  clinicianCode={clinicianCode}
                >
                  Cancel
                </PortalLink>
                <button
                  type="submit"
                  className="cp-button cp-button--primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
